import React, { useState } from 'react'
import { Platform, Vibration, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { Instance } from 'mobx-state-tree'
import { shuffle } from 'lodash'

import { GameQuestion } from '@trivie/core/src/models/game/GameQuestion'
import { Branding } from '@trivie/core/src/models/Branding'

import { Button } from '../button'
import { Text } from '../text'
import { color } from '../../theme/color'

import DraggableFlatList, { RenderItemParams } from 'react-native-draggable-flatlist'
import { LinearTransition } from 'react-native-reanimated'
import { iconDragDots } from '../../assets'
import { SvgIcon } from '../svg-icon'

interface OrderedChoicesProps {
  question: Instance<typeof GameQuestion>
  branding: Instance<typeof Branding>
  style?: ViewStyle
}

export const OrderedChoices = observer((props: OrderedChoicesProps) => {
  const { question, branding, style = {} } = props
  const [dataRight, setRightData] = useState(
    shuffle(
      question.ordering.map((o, i) => {
        return { text: o.text, index: i + 1 }
      }),
    ),
  )

  const handleSubmit = () => {
    if (question.answered) {
      return
    }
    question.setUserOrdering(dataRight)
    question.answer()
  }

  const renderItem = (params: RenderItemParams<any>) => {
    const { item, getIndex, drag, isActive } = params

    return (
      <View style={CHOICE_ROW}>
        <View style={IDX}>
          <Text
            disableLink
            variant="titleEmphasized"
            align="center"
            style={{ fontSize: 16, color: branding.text_color }}
            text={`${getIndex()! + 1}.`}
          />
        </View>
        <Button
          style={{
            ...CHOICE_BTN,
            borderColor: question.answered
              ? question.choices[0]?.ordering![getIndex()!].orderedCorrectly
                ? branding.correct_answer
                : branding.incorrect_answer
              : branding.accent_color,
            backgroundColor: branding.quiz_background,
          }}
          key={item.text}
          variant="outlined"
          fullWidth
          round
          onPressIn={drag}
        >
          <View style={HANLDE_CONTAINER}>
            <View style={HANDLE_ICON}>
              {Platform.OS === 'web' ? (
                <svg width="24" height="25" viewBox="0 0 24 25" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_211_23440)">
                    <path
                      fill={branding.text_color}
                      d="M11 18.5C11 19.6 10.1 20.5 9 20.5C7.9 20.5 7 19.6 7 18.5C7 17.4 7.9 16.5 9 16.5C10.1 16.5 11 17.4 11 18.5ZM9 10.5C7.9 10.5 7 11.4 7 12.5C7 13.6 7.9 14.5 9 14.5C10.1 14.5 11 13.6 11 12.5C11 11.4 10.1 10.5 9 10.5ZM9 4.5C7.9 4.5 7 5.4 7 6.5C7 7.6 7.9 8.5 9 8.5C10.1 8.5 11 7.6 11 6.5C11 5.4 10.1 4.5 9 4.5ZM15 8.5C16.1 8.5 17 7.6 17 6.5C17 5.4 16.1 4.5 15 4.5C13.9 4.5 13 5.4 13 6.5C13 7.6 13.9 8.5 15 8.5ZM15 10.5C13.9 10.5 13 11.4 13 12.5C13 13.6 13.9 14.5 15 14.5C16.1 14.5 17 13.6 17 12.5C17 11.4 16.1 10.5 15 10.5ZM15 16.5C13.9 16.5 13 17.4 13 18.5C13 19.6 13.9 20.5 15 20.5C16.1 20.5 17 19.6 17 18.5C17 17.4 16.1 16.5 15 16.5Z"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_211_23440">
                      <rect
                        fill={branding.text_color}
                        width="24"
                        height="24"
                        transform="translate(0 0.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              ) : (
                <SvgIcon size={24} src={iconDragDots} color={branding.text_color} />
              )}
            </View>
            <View style={CHOICE_TXT_CONTAINER}>
              <Text
                disableLink
                variant="titleMedium"
                align="center"
                style={{ color: branding.text_color }}
                text={item.text}
              />
            </View>
          </View>
        </Button>
      </View>
    )
  }

  const submitBtnStyle = {
    backgroundColor: question.answered
      ? question.answeredCorrectly
        ? branding.correct_answer
        : branding.incorrect_answer
      : branding.accent_color,
  }

  return (
    <View style={{ ...LIST, ...style }}>
      <DraggableFlatList
        activationDistance={1}
        itemLayoutAnimation={LinearTransition}
        // onPlaceholderIndexChange={() => Vibration.vibrate(20)}
        renderPlaceholder={() => (
          <View style={CHOICE_ROW}>
            <View style={{ width: 22 }} />
            <View style={{ ...PLACEHOLDER, borderColor: `${branding.accent_color}40` }} />
          </View>
        )}
        data={dataRight}
        contentContainerStyle={{ paddingLeft: 0 }}
        scrollEnabled={false}
        onDragEnd={({ data }) => setRightData(data)}
        keyExtractor={(item) => item.text}
        renderItem={renderItem}
      />
      <Button
        style={submitBtnStyle}
        textStyle={{ color: color.white }}
        fullWidth
        tx="Common.submit"
        onPress={handleSubmit}
      />
    </View>
  )
})

const LIST: ViewStyle = {
  maxWidth: 800,
  width: '100%',
  alignSelf: 'center',
  gap: 16,
  justifyContent: 'flex-end',
}
const PLACEHOLDER: ViewStyle = {
  borderWidth: 2,
  flex: 1,
  borderStyle: 'dashed',
  padding: 16,
  borderRadius: 24,
  justifyContent: 'center',
  marginVertical: 8,
  paddingHorizontal: 8,
}
const CHOICE_ROW: ViewStyle = {
  flexDirection: 'row',
  display: 'flex',
  gap: 12,
  flex: 1,
}
const CHOICE_BTN: ViewStyle = {
  // @ts-ignore
  cursor: 'move',
  borderColor: color.blue,
  padding: 0,
  borderLeftWidth: 29,
  height: 'auto',
  borderWidth: 2,
  borderRadius: 24,
  justifyContent: 'center',
  marginVertical: 8,
  zIndex: 3,
  minHeight: 56,
  flex: 1,
}
const IDX: ViewStyle = { width: 22, alignItems: 'center', justifyContent: 'center' }
const HANLDE_CONTAINER: ViewStyle = {
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
}
const HANDLE_ICON: ViewStyle = { position: 'absolute', left: -27 }
const CHOICE_TXT_CONTAINER: ViewStyle = { padding: 10, display: 'flex', flex: 1 }
